import { useEffect, useState, type JSX } from 'react';
import { PlusIcon, Trash2Icon, XIcon } from 'lucide-react';
import { apiGet, apiDelete, ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';

/**
 * Slice 3b chat-history wedge — left-rail list component.
 *
 * Renders chats grouped by `Vandaag / Gisteren / Deze week / Eerder` (NL
 * timezone). Title hierarchy per `/plan-design-review` D1A: title primary,
 * question preview secondary, timestamp + message-count metadata on right.
 *
 * Auto-title transition (D2A): pending titles render italic-gray; once the
 * server flips to `generated` the row cross-fades on next list refresh.
 *
 * Trash visibility (D7A): hover-reveal on desktop (md:opacity-0 →
 * group-hover:opacity-100); always-visible on mobile.
 *
 * Inline two-click delete (D2B): clicking trash swaps the row content to
 * `Weet je het zeker? [Verwijder] [Annuleer]`. No modal.
 */

interface ChatSummaryWire {
  chatId: string;
  title: string;
  titleStatus: 'pending' | 'generating' | 'generated';
  messageCount: number;
  sourceCount: number;
  lastQuestion: string;
  lastAnswerPreview: string;
  createdAt: string;
  updatedAt: string;
}

interface ListResponse {
  chats: ChatSummaryWire[];
  nextCursor: string | null;
}

interface ChatHistoryRailProps {
  currentChatId: string | null;
  onPickChat: (chatId: string) => void;
  onNewChat: () => void;
  /**
   * Bumped by the parent every time a chat is created or a turn finishes,
   * so the rail refetches and the new chat appears at the top of `Vandaag`.
   */
  refreshKey: number;
  /**
   * Mobile drawer state. The rail is always visible on md+ regardless;
   * `open` controls only the off-canvas behaviour at < md.
   */
  open?: boolean;
  onClose?: () => void;
}

const PAGE_SIZE = 50;

export function ChatHistoryRail({
  currentChatId,
  onPickChat,
  onNewChat,
  refreshKey,
  open = false,
  onClose,
}: ChatHistoryRailProps): JSX.Element {
  const { user } = useAuth();
  // Demo: de geseede gesprekken zijn leesvoer voor prospects; verwijderen is
  // daar geblokkeerd (read-only guard), dus de prullenbak blijft verborgen.
  const demo = user?.demo === true;
  const [chats, setChats] = useState<ChatSummaryWire[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Initial load + every refreshKey bump.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await apiGet<ListResponse>('/api/v1/chats', { limit: PAGE_SIZE });
        if (cancelled) return;
        setChats(res.chats);
        setNextCursor(res.nextCursor);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof ApiError
            ? 'Geschiedenis kon niet worden geladen.'
            : 'Geschiedenis kon niet worden geladen.',
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  async function loadMore(): Promise<void> {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await apiGet<ListResponse>('/api/v1/chats', {
        limit: PAGE_SIZE,
        cursor: nextCursor,
      });
      setChats((prev) => [...prev, ...res.chats]);
      setNextCursor(res.nextCursor);
    } catch {
      setError('Meer geschiedenis laden mislukte.');
    } finally {
      setLoadingMore(false);
    }
  }

  async function confirmDelete(chatId: string): Promise<void> {
    try {
      await apiDelete(`/api/v1/chats/${chatId}`);
      setChats((prev) => prev.filter((c) => c.chatId !== chatId));
      setConfirmDeleteId(null);
      if (currentChatId === chatId) onNewChat();
    } catch {
      setError('Verwijderen mislukte. Probeer het opnieuw.');
      setConfirmDeleteId(null);
    }
  }

  const groups = groupByBucket(chats);

  return (
    <>
      {/* Mobile scrim — only when drawer is open at < md. */}
      {open && (
        <button
          type="button"
          aria-label="Sluit geschiedenis"
          onClick={onClose}
          className="fixed inset-0 z-30 bg-ink-900/40 md:hidden"
        />
      )}
      <aside
        aria-label="Geschiedenis"
        className={`fixed inset-y-0 left-0 z-40 flex w-[272px] shrink-0 flex-col border-r border-brand-100 bg-white transition-transform duration-200 md:static md:translate-x-0 ${
          open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
      >
        {/* Mobile-only header strip with the close button — sits ABOVE the
            "Nieuw gesprek" button so the two don't overlap. Hidden on md+. */}
        <div className="flex items-center justify-between border-b border-brand-100 px-3 py-2 md:hidden">
          <span className="text-xs font-semibold uppercase tracking-wide text-ink-700/70">
            Geschiedenis
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sluit geschiedenis"
            className="rounded-md p-1.5 text-ink-900/60 hover:bg-brand-50 hover:text-ink-900"
          >
            <XIcon size={18} />
          </button>
        </div>
        <div className="border-b border-brand-100 px-4 py-3">
          <button
            type="button"
            onClick={onNewChat}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-500 px-3 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-600"
          >
            <PlusIcon className="h-4 w-4" /> Nieuw gesprek
          </button>
          {/* "Geschiedenis" heading is hidden on mobile (already shown in the
              top strip) but stays visible on desktop where the rail has no
              top strip. */}
          <h2 className="mt-4 hidden text-sm font-semibold text-ink-700 md:block">Geschiedenis</h2>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && chats.length === 0 && <SkeletonRows />}

          {!loading && chats.length === 0 && error === null && (
            <div className="px-4 py-12 text-center">
              <p className="text-sm font-semibold text-ink-700">Nog geen gesprekken.</p>
              <p className="mt-1 text-xs text-ink-500">Stel je eerste vraag om te beginnen.</p>
            </div>
          )}

          {error !== null && (
            <div className="px-4 py-6">
              <p className="text-sm text-error">{error}</p>
              <button
                type="button"
                onClick={() => setError(null)}
                className="mt-2 text-xs text-brand-600 hover:underline"
              >
                Opnieuw proberen
              </button>
            </div>
          )}

          {groups.map((group) => (
            <div key={group.label} className="py-2">
              <div className="sticky top-0 z-10 bg-white px-4 py-1 text-[11px] font-medium uppercase tracking-wide text-ink-500">
                {group.label}
              </div>
              <ul className="px-1.5">
                {group.chats.map((chat) => (
                  <li key={chat.chatId} className="group">
                    {confirmDeleteId === chat.chatId ? (
                      <ConfirmDeleteRow
                        onConfirm={() => void confirmDelete(chat.chatId)}
                        onCancel={() => setConfirmDeleteId(null)}
                      />
                    ) : (
                      <ChatRow
                        chat={chat}
                        isActive={currentChatId === chat.chatId}
                        onPick={() => onPickChat(chat.chatId)}
                        onAskDelete={demo ? null : () => setConfirmDeleteId(chat.chatId)}
                      />
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {nextCursor !== null && (
            <div className="px-3 py-3">
              <button
                type="button"
                onClick={() => void loadMore()}
                disabled={loadingMore}
                className="w-full rounded-md py-2 text-xs font-medium text-ink-500 transition hover:bg-brand-50 hover:text-ink-700 disabled:opacity-60"
              >
                {loadingMore ? 'Laden…' : 'Meer laden'}
              </button>
            </div>
          )}
        </div>

        <div className="border-t border-brand-50 px-4 py-4">
          <div className="rounded-lg border border-brand-100 bg-brand-50/70 p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-800">
              Vertrouwensmodel
            </div>
            <ul className="mt-2 space-y-1.5 text-[11px] leading-snug text-ink-900/70">
              {(user?.role === 'suppohrt_admin' || user?.role === 'suppohrt_medewerker') && (
                <li className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-slate-500" />
                  Ring 0 — SUPPOHRT Intern
                </li>
              )}
              <li className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-brand-500" />
                Ring 1 — Eigen bronnen
              </li>
              <li className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-ring2-fg" />
                Ring 2 — NL geverifieerd
              </li>
            </ul>
          </div>
        </div>
      </aside>
    </>
  );
}

interface ChatRowProps {
  chat: ChatSummaryWire;
  isActive: boolean;
  onPick: () => void;
  /** null verbergt de verwijderknop (demo). */
  onAskDelete: (() => void) | null;
}

function ChatRow({ chat, isActive, onPick, onAskDelete }: ChatRowProps): JSX.Element {
  const titleIsPending = chat.titleStatus !== 'generated';
  return (
    <div
      className={`relative cursor-pointer rounded-md px-3 py-2 transition ${
        isActive ? 'bg-brand-50 border-l-2 border-brand-500 pl-[10px]' : 'hover:bg-brand-50/50'
      }`}
    >
      <button
        type="button"
        onClick={onPick}
        className="block w-full text-left"
        aria-label={`Open gesprek: ${chat.title}`}
      >
        <p
          className={`line-clamp-2 text-sm font-medium transition-opacity ${
            titleIsPending ? 'italic text-ink-500' : 'not-italic text-ink-700'
          }`}
        >
          {titleIsPending && chat.titleStatus === 'pending' && chat.messageCount === 0
            ? 'Nieuw gesprek…'
            : chat.title}
        </p>
        {chat.lastQuestion && (
          <p className="mt-0.5 truncate text-xs text-ink-500">{chat.lastQuestion}</p>
        )}
        <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-ink-500">
          <span>{formatTime(chat.updatedAt)}</span>
          <span className="rounded-sm bg-brand-100 px-1.5 py-0.5 tabular">
            {chat.messageCount} ber.
          </span>
        </div>
      </button>
      {onAskDelete !== null && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onAskDelete();
          }}
          aria-label="Verwijder gesprek"
          className="absolute right-2 top-2 rounded p-1 text-ink-500 opacity-100 transition hover:bg-error/10 hover:text-error md:opacity-0 md:group-hover:opacity-100"
        >
          <Trash2Icon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

function ConfirmDeleteRow({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  return (
    <div className="rounded-md bg-error/5 px-3 py-2.5">
      <p className="text-sm font-medium text-ink-700">Weet je het zeker?</p>
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          onClick={onConfirm}
          className="rounded bg-error px-3 py-1 text-xs font-semibold text-white hover:bg-error/90"
        >
          Verwijder
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-ink-100 px-3 py-1 text-xs text-ink-700 hover:bg-brand-50"
        >
          Annuleer
        </button>
      </div>
    </div>
  );
}

function SkeletonRows(): JSX.Element {
  return (
    <div className="space-y-2 px-3 py-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="rounded-md bg-brand-100/60 px-3 py-3">
          <div className="h-3 w-3/4 rounded bg-brand-100 animate-pulse" />
          <div className="mt-2 h-2.5 w-1/2 rounded bg-brand-100 animate-pulse" />
        </div>
      ))}
    </div>
  );
}

// ─────── grouping + formatting helpers ──────────────────────────────────

interface BucketGroup {
  label: string;
  chats: ChatSummaryWire[];
}

const AMS_TIMEZONE = 'Europe/Amsterdam';

function groupByBucket(chats: ChatSummaryWire[]): BucketGroup[] {
  const today: ChatSummaryWire[] = [];
  const yesterday: ChatSummaryWire[] = [];
  const thisWeek: ChatSummaryWire[] = [];
  const earlier: ChatSummaryWire[] = [];
  const now = new Date();
  const startOfToday = startOfDayAms(now);
  const startOfYesterday = new Date(startOfToday.getTime() - 24 * 60 * 60 * 1000);
  const startOfThisWeek = new Date(startOfToday.getTime() - 7 * 24 * 60 * 60 * 1000);
  for (const chat of chats) {
    const t = new Date(chat.updatedAt).getTime();
    if (t >= startOfToday.getTime()) today.push(chat);
    else if (t >= startOfYesterday.getTime()) yesterday.push(chat);
    else if (t >= startOfThisWeek.getTime()) thisWeek.push(chat);
    else earlier.push(chat);
  }
  const out: BucketGroup[] = [];
  if (today.length) out.push({ label: 'Vandaag', chats: today });
  if (yesterday.length) out.push({ label: 'Gisteren', chats: yesterday });
  if (thisWeek.length) out.push({ label: 'Deze week', chats: thisWeek });
  if (earlier.length) out.push({ label: 'Eerder', chats: earlier });
  return out;
}

function startOfDayAms(d: Date): Date {
  // Render the date in NL timezone, then parse back to a UTC Date that
  // represents 00:00 AMS on that day. Good enough for bucket boundaries.
  const ymd = new Intl.DateTimeFormat('en-CA', {
    timeZone: AMS_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
  return new Date(`${ymd}T00:00:00+02:00`); // CET/CEST simplification — wedge scope
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const today = startOfDayAms(new Date());
  if (d.getTime() >= today.getTime()) {
    return new Intl.DateTimeFormat('nl-NL', {
      timeZone: AMS_TIMEZONE,
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  }
  return new Intl.DateTimeFormat('nl-NL', {
    timeZone: AMS_TIMEZONE,
    day: 'numeric',
    month: 'short',
  }).format(d);
}
